import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Megaphone, Plus, Pin, Edit, Trash2, CheckCircle, XCircle } from 'lucide-react';

const AUDIENCES = [
  { value: 'all',        label: 'Everyone' },
  { value: 'teachers',   label: 'All Teachers' },
  { value: 'students',   label: 'All Students' },
  { value: 'parents',    label: 'All Parents' },
  { value: 'department:Coding',         label: 'Department: Coding' },
  { value: 'department:Robotics',       label: 'Department: Robotics' },
  { value: 'department:AI',             label: 'Department: AI' },
  { value: 'department:Mathematics',    label: 'Department: Mathematics' },
  { value: 'department:IELTS',          label: 'Department: IELTS' },
  { value: 'department:Spoken Mastery', label: 'Department: Spoken Mastery' },
];

const audienceLabel = (val) => (AUDIENCES.find(a => a.value === val)?.label) || val;

export const AnnouncementsManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);     // null = create mode
  const [form, setForm] = useState({ title: '', body_html: '', audience: 'all', pinned: false, ends_at: '' });
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/announcements');
      setItems(res.data || []);
    } catch (e) {
      toast.error('Failed to load announcements');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openCreate = () => {
    setEditing(null);
    setForm({ title: '', body_html: '', audience: 'all', pinned: false, ends_at: '' });
    setDialogOpen(true);
  };

  const openEdit = (a) => {
    setEditing(a);
    setForm({
      title: a.title || '',
      body_html: a.body_html || '',
      audience: a.audience || 'all',
      pinned: !!a.pinned,
      ends_at: a.ends_at ? a.ends_at.slice(0, 16) : '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error('Title is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        body_html: form.body_html,
        audience: form.audience,
        pinned: form.pinned,
        ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : null,
      };
      if (editing) {
        await api.put(`/admin/announcements/${editing.id}`, payload);
        toast.success('Announcement updated');
      } else {
        await api.post('/admin/announcements', payload);
        toast.success('Announcement published');
      }
      setDialogOpen(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Archive this announcement? It will be hidden from all users.')) return;
    try {
      await api.delete(`/admin/announcements/${id}`);
      toast.success('Archived');
      fetchAll();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const togglePin = async (a) => {
    try {
      await api.put(`/admin/announcements/${a.id}`, { pinned: !a.pinned });
      fetchAll();
    } catch { toast.error('Update failed'); }
  };

  const visible = items.filter(a => !a.archived);
  const archived = items.filter(a => a.archived);

  return (
    <>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Megaphone className="w-7 h-7 text-blue-600" /> Announcements
          </h1>
          <p className="text-slate-500 font-semibold">Broadcast messages to teachers, students, parents, or specific departments</p>
        </div>
        <Button className="bg-blue-600 hover:bg-blue-700 font-bold" onClick={openCreate} data-testid="create-announcement-btn">
          <Plus className="w-4 h-4 mr-1" /> New Announcement
        </Button>
      </div>

      <div className="nb-card overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-blue-500/10">
              <TableHead className="text-blue-700 font-bold">Title</TableHead>
              <TableHead className="text-blue-700 font-bold">Audience</TableHead>
              <TableHead className="text-blue-700 font-bold text-center">Pinned</TableHead>
              <TableHead className="text-blue-700 font-bold">Created</TableHead>
              <TableHead className="text-blue-700 font-bold text-center">Reads</TableHead>
              <TableHead className="text-blue-700 font-bold text-center">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-6">Loading…</TableCell></TableRow>
            ) : visible.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-8">No announcements yet. Click "New Announcement" to publish your first message.</TableCell></TableRow>
            ) : visible.map(a => (
              <TableRow key={a.id} data-testid={`announcement-row-${a.id}`}>
                <TableCell>
                  <div className="font-bold text-slate-900">{a.title}</div>
                  <div className="text-xs text-slate-500 line-clamp-1" dangerouslySetInnerHTML={{ __html: a.body_html }} />
                </TableCell>
                <TableCell><span className="px-2 py-0.5 rounded-full text-xs font-bold bg-violet-100 text-violet-700">{audienceLabel(a.audience)}</span></TableCell>
                <TableCell className="text-center">
                  <button onClick={() => togglePin(a)} title={a.pinned ? 'Unpin' : 'Pin'} data-testid={`pin-toggle-${a.id}`}
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold border-2 ${a.pinned ? 'bg-amber-100 text-amber-700 border-amber-600' : 'bg-slate-100 text-slate-600 border-slate-300'}`}>
                    <Pin className="w-3 h-3" /> {a.pinned ? 'Pinned' : 'Pin'}
                  </button>
                </TableCell>
                <TableCell className="text-xs">
                  <div className="text-slate-700">{a.author_name}</div>
                  <div className="text-slate-400">{new Date(a.created_at).toLocaleString()}</div>
                </TableCell>
                <TableCell className="text-center font-bold text-blue-600">{(a.read_by || []).length}</TableCell>
                <TableCell className="text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Button size="sm" variant="outline" className="h-7 px-2 border-amber-400 text-amber-700 hover:bg-amber-50" onClick={() => openEdit(a)} data-testid={`edit-announcement-${a.id}`}>
                      <Edit className="w-3.5 h-3.5 mr-1" /> Edit
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 px-2 border-red-400 text-red-700 hover:bg-red-50" onClick={() => handleDelete(a.id)} data-testid={`delete-announcement-${a.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {archived.length > 0 && (
        <div className="mt-6">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-2">Archived ({archived.length})</h3>
          <div className="space-y-1">
            {archived.map(a => (
              <div key={a.id} className="px-3 py-2 rounded-lg bg-slate-100 text-slate-500 text-xs flex items-center justify-between">
                <span>{a.title} <span className="text-slate-400">— {audienceLabel(a.audience)}</span></span>
                <span className="text-slate-400">{new Date(a.updated_at).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-bold">{editing ? 'Edit Announcement' : 'New Announcement'}</DialogTitle>
            <DialogDescription>Compose a message and pick who sees it. Pinned items show as a sticky banner.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="font-bold">Title *</Label>
              <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                placeholder="e.g. Holiday schedule update" className="mt-1" data-testid="announcement-title" />
            </div>
            <div>
              <Label className="font-bold">Body</Label>
              <Textarea value={form.body_html} onChange={e => setForm(p => ({ ...p, body_html: e.target.value }))}
                placeholder="Write your message. HTML allowed: <p>, <strong>, <em>, <a>, <ul>/<li>, <br>"
                className="mt-1 min-h-[140px]" data-testid="announcement-body" />
              <p className="text-[11px] text-slate-400 mt-1">Tip: wrap paragraphs in &lt;p&gt;…&lt;/p&gt; or use blank lines for spacing.</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="font-bold">Audience</Label>
                <Select value={form.audience} onValueChange={v => setForm(p => ({ ...p, audience: v }))}>
                  <SelectTrigger className="mt-1" data-testid="announcement-audience"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {AUDIENCES.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-bold">Expires at (optional)</Label>
                <Input type="datetime-local" value={form.ends_at}
                  onChange={e => setForm(p => ({ ...p, ends_at: e.target.value }))}
                  className="mt-1" data-testid="announcement-ends-at" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="pinned" checked={form.pinned}
                onChange={e => setForm(p => ({ ...p, pinned: e.target.checked }))}
                className="w-4 h-4" data-testid="announcement-pinned" />
              <Label htmlFor="pinned" className="font-bold cursor-pointer">
                Pin as banner — show at top of recipient dashboards
              </Label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="cancel-announcement">
                <XCircle className="w-4 h-4 mr-1" /> Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 font-bold" data-testid="save-announcement">
                <CheckCircle className="w-4 h-4 mr-1" /> {saving ? 'Saving…' : (editing ? 'Update' : 'Publish')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AnnouncementsManager;
